import React, { useEffect, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import * as XLSX from 'xlsx';
import mammoth from 'mammoth';
import JSZip from 'jszip';
import { DocumentNode } from '../../types/documents';
import { documentDownloadService } from '../../services/documentDownloadService';
import { documentSignatureService } from '../../services/documentSignatureService';

const readOnlyModules = { toolbar: false };

const readOnlyQuillStyles = `
.quill-readonly-content .ql-toolbar.ql-snow{display:none}
.quill-readonly-content .ql-container.ql-snow{border:0;font-size:.875rem}
.quill-readonly-content .ql-editor{padding:1rem;min-height:120px;white-space:pre-wrap;word-break:break-word}
`;

interface DocumentViewerProps {
  document: DocumentNode;
  onClose: () => void;
  /** Quando true, o visualizador ocupa o fluxo da página (painel) em vez de modal em tela cheia. */
  embedded?: boolean;
  /** ID da signature request para carregar PDF parcial/final assinado quando disponível. */
  signatureRequestId?: string;
  /**
   * Quando true, tenta o PDF assinado com retries antes de cair no original —
   * evita flash sem assinaturas; após esgotar tentativas, mostra original com aviso.
   */
  preferSignedDocument?: boolean;
  /** Backend marcou falha na geração da prévia parcial. */
  partialSignedDocumentFailed?: boolean;
  /** Estado da geração do PDF consolidado (overlay no viewer). */
  signedDocumentStatus?: 'ready' | 'processing' | 'failed' | null;
  /** Callback para reenfileirar geração do PDF final após falha. */
  onRetryFinalPdf?: () => void;
  /** Callback para reenfileirar geração da prévia parcial após falha. */
  onRetryPartialPdf?: () => void;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({
  document,
  onClose,
  embedded = false,
  signatureRequestId,
  preferSignedDocument = false,
  partialSignedDocumentFailed = false,
  signedDocumentStatus = null,
  onRetryFinalPdf,
  onRetryPartialPdf,
}) => {
  const [content, setContent] = useState<string>('');
  const [edocHtml, setEdocHtml] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [awaitingSignedPreview, setAwaitingSignedPreview] = useState(false);
  const [signedPreviewFailed, setSignedPreviewFailed] = useState(false);
  const viewerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void loadDocument();
  }, [document, signatureRequestId, preferSignedDocument, partialSignedDocumentFailed]);

  // Adicionar suporte para navegação por teclado e scroll
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const viewer = viewerRef.current;
      if (!viewer) return;

      switch (event.key) {
        case 'ArrowUp':
          viewer.scrollBy(0, -50);
          event.preventDefault();
          break;
        case 'ArrowDown':
          viewer.scrollBy(0, 50);
          event.preventDefault();
          break;
        case 'ArrowLeft':
          viewer.scrollBy(-50, 0);
          event.preventDefault();
          break;
        case 'ArrowRight':
          viewer.scrollBy(50, 0);
          event.preventDefault();
          break;
        case 'PageUp':
          viewer.scrollBy(0, -viewer.clientHeight * 0.8);
          event.preventDefault();
          break;
        case 'PageDown':
          viewer.scrollBy(0, viewer.clientHeight * 0.8);
          event.preventDefault();
          break;
        case 'Home':
          viewer.scrollTo(0, 0);
          event.preventDefault();
          break;
        case 'End':
          viewer.scrollTo(viewer.scrollWidth, viewer.scrollHeight);
          event.preventDefault();
          break;
        case 'Escape':
          onClose();
          event.preventDefault();
          break;
      }
    };

    const handleScroll = () => {
      const viewer = viewerRef.current;
      if (!viewer) return;

      const scrollIndicator = window.document.getElementById('scroll-indicator') as HTMLElement;
      if (scrollIndicator) {
        const scrollTop = viewer.scrollTop;
        const scrollHeight = viewer.scrollHeight - viewer.clientHeight;
        const scrollPercentage = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;
        scrollIndicator.style.width = `${scrollPercentage}%`;
      }
    };

    const viewer = viewerRef.current;
    if (viewer) {
      (viewer as HTMLElement).addEventListener('scroll', handleScroll);
    }

    window.document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.document.removeEventListener('keydown', handleKeyDown);
      if (viewer) {
        (viewer as HTMLElement).removeEventListener('scroll', handleScroll);
      }
    };
  }, [onClose]);

  const getFileExtension = (node: DocumentNode): string => {
    const fileName = node.metadata?.fileName || node.title || '';
    return fileName.split('.').pop()?.toLowerCase() || '';
  };

  const getFileType = (node: DocumentNode): string => {
    const ext = getFileExtension(node);

    if (ext === 'pdf' || node.nodeType === 'pdf')
    {
      return 'pdf';
    }
    if (ext === 'html' || ext === 'htm')
    {
      return 'html';
    }
    if (ext === 'txt')
    {
      return 'text';
    }
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(ext))
    {
      return 'image';
    }
    if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(ext))
    {
      return 'video';
    }
    if (['mp3', 'wav', 'ogg', 'aac', 'flac'].includes(ext))
    {
      return 'audio';
    }
    if (['doc', 'docx'].includes(ext))
    {
      return 'word';
    }
    if (['xls', 'xlsx'].includes(ext))
    {
      return 'excel';
    }
    if (['odt'].includes(ext))
    {
      return 'odt';
    }
    if (['odg'].includes(ext))
    {
      return 'odg';
    }
    if (['odp'].includes(ext))
    {
      return 'odp';
    }
    if (['edoc'].includes(ext) || node.nodeType === 'edoc')
    {
      return 'edoc';
    }
    if (['zip', '7z', 'rar', 'tar', 'gz', 'bz2'].includes(ext))
    {
      return 'compressed';
    }
    return 'other';
  };

  const loadDocument = async (options?: { retrySignedPreview?: boolean }) => {
    try {
      setIsLoading(true);
      setError('');
      setEdocHtml(null);
      setAwaitingSignedPreview(false);
      if (options?.retrySignedPreview) {
        setSignedPreviewFailed(false);
      }

      if (signatureRequestId) {
        const knownPartialFailure = preferSignedDocument && partialSignedDocumentFailed;
        const maxAttempts = knownPartialFailure ? 1 : preferSignedDocument ? 15 : 8;
        const delayMs = preferSignedDocument ? 2000 : 1500;
        setAwaitingSignedPreview(preferSignedDocument && !knownPartialFailure);

        for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
          try {
            try {
              const finalBlob = await documentSignatureService.fetchSignedFinalPdfBlob(
                signatureRequestId
              );
              await loadPDF(finalBlob);
              setAwaitingSignedPreview(false);
              setSignedPreviewFailed(false);
              return;
            } catch {
              const partialBlob = await documentSignatureService.fetchPartialSignedPdfBlob(
                signatureRequestId
              );
              await loadPDF(partialBlob);
              setAwaitingSignedPreview(false);
              setSignedPreviewFailed(false);
              return;
            }
          } catch (err) {
            const isLastAttempt = attempt >= maxAttempts - 1;
            if (isLastAttempt) {
              console.warn(
                'PDF assinado não disponível após tentativas, usando documento original:',
                err
              );
              if (preferSignedDocument) {
                setSignedPreviewFailed(true);
              }
              break;
            }
            await new Promise((resolve) => setTimeout(resolve, delayMs));
          }
        }
      }

      setAwaitingSignedPreview(false);

      const fileType = getFileType(document);
      const blob = await documentDownloadService.downloadDocument(document.id);
      
      switch (fileType) {
        case 'pdf':
          await loadPDF(blob);
          break;
        case 'excel':
          await loadExcel(blob);
          break;
        case 'word':
          await loadWord(blob);
          break;
        case 'html':
          await loadHTML(blob, 'html');
          break;
        case 'edoc':
          await loadHTML(blob, 'edoc');
          break;
        case 'text':
          await loadText(blob);
          break;
        case 'odt':
          await loadODT(blob);
          break;
        case 'odg':
        case 'odp':
          await loadOpenDocument(blob, fileType);
          break;
        case 'image':
          await loadImage(blob);
          break;
        default:
          setError(`Tipo de arquivo não suportado: ${fileType}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
    } finally {
      setIsLoading(false);
    }
  };

  const loadPDF = async (blob: Blob) => {
    const url = URL.createObjectURL(blob);
    setContent(`
      <iframe 
        src="${url}" 
        width="100%" 
        height="600px" 
        frameborder="0"
        style="border: none;"
      ></iframe>
    `);
  };

  const loadExcel = async (blob: Blob) => {
    try {
      const arrayBuffer = await blob.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      
      let htmlContent = '<div class="excel-viewer">';
      
      // Processar cada planilha
      workbook.SheetNames.forEach(sheetName => {
        const worksheet = workbook.Sheets[sheetName];
        const html = XLSX.utils.sheet_to_html(worksheet, { 
          id: sheetName,
          editable: false,
          header: '',
          footer: ''
        });
        
        htmlContent += `
          <div class="sheet-container">
            <h3 class="sheet-title">${sheetName}</h3>
            <div class="sheet-content">${html}</div>
          </div>
        `;
      });
      
      htmlContent += '</div>';
      
      // Adicionar estilos CSS para melhor visualização
      const styledContent = `
        <style>
          .excel-viewer {
            font-family: Arial, sans-serif;
            padding: 20px;
            min-width: 100%;
            overflow-x: auto;
          }
          .sheet-container {
            margin-bottom: 30px;
            border: 1px solid #ddd;
            border-radius: 8px;
            overflow: hidden;
            min-width: fit-content;
          }
          .sheet-title {
            background: #f5f5f5;
            margin: 0;
            padding: 10px 15px;
            border-bottom: 1px solid #ddd;
            font-size: 16px;
            font-weight: bold;
            position: sticky;
            top: 0;
            z-index: 10;
          }
          .sheet-content {
            overflow-x: auto;
            overflow-y: visible;
            max-height: 80vh;
          }
          .sheet-content table {
            border-collapse: collapse;
            width: max-content;
            min-width: 100%;
            margin: 0;
          }
          .sheet-content td, .sheet-content th {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
            min-width: 100px;
            white-space: nowrap;
            position: relative;
          }
          .sheet-content th {
            background-color: #f2f2f2;
            font-weight: bold;
            position: sticky;
            top: 0;
            z-index: 5;
          }
          .sheet-content td:hover, .sheet-content th:hover {
            background-color: #f8f9fa;
          }
        </style>
        ${htmlContent}
      `;
      
      setContent(styledContent);
    } catch (error) {
      setError('Erro ao processar arquivo Excel');
    }
  };

  const loadWord = async (blob: Blob) => {
    try {
      const arrayBuffer = await blob.arrayBuffer();
      const fileExtension = getFileExtension(document);
      
      // Verificar se é .doc ou .docx
      if (fileExtension === 'doc') {
        // Para arquivos .doc, mostrar mensagem informativa
        setContent(`
          <div style="padding: 40px; text-align: center; font-family: Arial, sans-serif;">
            <div style="background: #fff3cd; border: 1px solid #ffeaa7; border-radius: 8px; padding: 20px; margin: 20px 0;">
              <h3 style="color: #856404; margin: 0 0 10px 0;">Arquivo .doc detectado</h3>
              <p style="color: #856404; margin: 0;">
                Arquivos .doc (formato antigo) não podem ser visualizados diretamente no navegador. 
                Para visualizar este documento, use a opção <strong>Baixar</strong> e abra com o Microsoft Word.
              </p>
            </div>
            <div style="background: #f8f9fa; border-radius: 8px; padding: 20px; margin: 20px 0;">
              <h4 style="color: #495057; margin: 0 0 15px 0;">Alternativas:</h4>
              <ul style="text-align: left; color: #6c757d; max-width: 400px; margin: 0 auto;">
                <li>Baixar o arquivo e abrir com Microsoft Word</li>
                <li>Converter o arquivo para .docx e fazer upload novamente</li>
                <li>Usar o Microsoft Office Online para visualizar</li>
              </ul>
            </div>
          </div>
        `);
        return;
      }
      
      // Para arquivos .docx, usar mammoth
      const result = await mammoth.convertToHtml({ arrayBuffer });
      
      const styledContent = `
        <style>
          .word-viewer {
            font-family: 'Times New Roman', serif;
            line-height: 1.6;
            padding: 20px;
            max-width: 100%;
            margin: 0 auto;
            background: white;
            box-shadow: 0 0 10px rgba(0,0,0,0.1);
            border-radius: 8px;
            overflow-x: auto;
            min-height: 100%;
          }
          .word-viewer h1, .word-viewer h2, .word-viewer h3 {
            color: #333;
            margin-top: 20px;
            margin-bottom: 10px;
            word-wrap: break-word;
          }
          .word-viewer p {
            margin-bottom: 10px;
            word-wrap: break-word;
            overflow-wrap: break-word;
          }
          .word-viewer table {
            border-collapse: collapse;
            width: 100%;
            margin: 15px 0;
            overflow-x: auto;
            display: block;
            white-space: nowrap;
          }
          .word-viewer table tbody {
            display: table;
            width: 100%;
          }
          .word-viewer td, .word-viewer th {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
            white-space: nowrap;
          }
          .word-viewer th {
            background-color: #f2f2f2;
          }
          .word-viewer img {
            max-width: 100%;
            height: auto;
          }
          .word-viewer pre {
            overflow-x: auto;
            background: #f8f9fa;
            padding: 10px;
            border-radius: 4px;
            white-space: pre-wrap;
          }
        </style>
        <div class="word-viewer">
          ${result.value}
        </div>
      `;
      
      setContent(styledContent);
    } catch (error) {
      setError('Erro ao processar arquivo Word. Tente fazer o download do arquivo.');
    }
  };

  const loadHTML = async (blob: Blob, kind: 'html' | 'edoc') => {
    try {
      const text = await blob.text();

      if (kind === 'edoc') {
        setEdocHtml(text);
        setContent('');
        return;
      }

      setEdocHtml(null);
      setContent(`
        <div style="padding: 20px; font-family: Arial, sans-serif;">
          ${text}
        </div>
      `);
    } catch (error) {
      setError('Erro ao processar conteúdo HTML');
    }
  };

  const loadText = async (blob: Blob) => {
    const text = await blob.text();
    setContent(`
      <div style="padding: 20px; font-family: 'Courier New', monospace; white-space: pre-wrap; background: #f5f5f5; border-radius: 8px;">
        ${text}
      </div>
    `);
  };

  const loadODT = async (blob: Blob) => {
    // Para ODT, vamos tentar extrair o conteúdo como texto simples
    try {
      const arrayBuffer = await blob.arrayBuffer();
      const zip = new JSZip();
      const zipFile = await zip.loadAsync(arrayBuffer);
      
      // Ler o arquivo content.xml do ODT
      const contentXml = await zipFile.file('content.xml')?.async('text');
      if (contentXml) {
        // Extrair texto simples do XML (implementação básica)
        const textContent = contentXml
          .replace(/<[^>]*>/g, '') // Remove tags XML
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&amp;/g, '&')
          .replace(/&quot;/g, '"')
          .replace(/&#39;/g, "'");
        
        setContent(`
          <div style="padding: 20px; font-family: Arial, sans-serif; line-height: 1.6;">
            <h3>Conteúdo do documento ODT:</h3>
            <div style="background: white; padding: 15px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
              ${textContent}
            </div>
          </div>
        `);
      } else {
        setError('Não foi possível extrair o conteúdo do arquivo ODT');
      }
    } catch (error) {
      setError('Erro ao processar arquivo ODT');
    }
  };

  const loadOpenDocument = async (blob: Blob, fileType: string) => {
    setError(`Visualização de arquivos ${fileType.toUpperCase()} não implementada. Use a opção de download.`);
  };

  const loadImage = async (blob: Blob) => {
    const url = URL.createObjectURL(blob);
    setContent(`
      <div style="text-align: center; padding: 20px;">
        <img src="${url}" style="max-width: 100%; max-height: 600px; border-radius: 8px; box-shadow: 0 4px 8px rgba(0,0,0,0.1);" />
      </div>
    `);
  };

  if (isLoading || awaitingSignedPreview) {
    const outerClass = embedded
      ? 'flex w-full flex-1 min-h-[320px] flex-col'
      : 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';
    const innerClass = embedded
      ? 'flex flex-1 flex-col items-center justify-center rounded-lg border border-gray-200 bg-white p-8'
      : 'bg-white rounded-lg p-6 max-w-md w-full mx-4';
    return (
      <div className={outerClass}>
        <div className={innerClass}>
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-900 font-medium">
              {awaitingSignedPreview || preferSignedDocument
                ? 'Atualizando documento com as assinaturas…'
                : 'Carregando documento...'}
            </p>
            {(awaitingSignedPreview || preferSignedDocument) && (
              <p className="mt-2 text-sm text-gray-500">
                Isso pode levar alguns segundos. A prévia com evidências visuais será exibida em seguida.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    const outerClass = embedded
      ? 'flex w-full flex-1 min-h-[320px] flex-col'
      : 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';
    const innerClass = embedded
      ? 'flex flex-1 flex-col items-center justify-center rounded-lg border border-red-100 bg-white p-8'
      : 'bg-white rounded-lg p-6 max-w-md w-full mx-4';
    return (
      <div className={outerClass}>
        <div className={innerClass}>
          <div className="text-center">
            <div className="text-red-500 text-4xl mb-4">⚠️</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Erro ao carregar documento</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={onClose}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    );
  }

  const shellOuterClass = embedded
    ? 'flex h-full min-h-0 w-full flex-col overflow-hidden'
    : 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';
  const shellInnerClass = embedded
    ? 'flex flex-col flex-1 min-h-0 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm'
    : 'bg-white rounded-lg w-[95vw] h-[95vh] flex flex-col shadow-2xl';

  return (
    <div className={shellOuterClass}>
      <div className={shellInnerClass}>
        {/* Header — oculto no modo embutido */}
        {!embedded && (
        <div className="flex justify-between items-center p-4 border-b bg-gray-50 rounded-t-lg">
          <div className="flex items-center space-x-3">
            <h3 className="text-lg font-semibold text-gray-900 truncate">
              {document.title}
            </h3>
            <span className="text-sm text-gray-500 bg-gray-200 px-2 py-1 rounded">
              {getFileExtension(document).toUpperCase()}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => window.print()}
              className="text-gray-500 hover:text-gray-700 p-2 rounded hover:bg-gray-100"
              title="Imprimir"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl p-2 rounded hover:bg-gray-100"
              title="Fechar"
            >
              ×
            </button>
          </div>
        </div>
        )}
        
        {/* Content Area with Enhanced Scrolling */}
        <div className="flex-1 overflow-hidden relative flex flex-col">
          {signedPreviewFailed && (
            <div className="shrink-0 z-30 border-b border-amber-200 bg-amber-50 px-4 py-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs leading-relaxed text-amber-950 sm:text-sm">
                  Não foi possível carregar a prévia com assinaturas. Exibindo o documento original.
                  As assinaturas já estão registradas.
                </p>
                {(onRetryPartialPdf || signatureRequestId) && (
                  <button
                    type="button"
                    onClick={() => {
                      if (onRetryPartialPdf) {
                        onRetryPartialPdf();
                        return;
                      }
                      void loadDocument({ retrySignedPreview: true });
                    }}
                    className="shrink-0 rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700"
                  >
                    Tentar prévia novamente
                  </button>
                )}
              </div>
            </div>
          )}
          <div className="relative min-h-0 flex-1 overflow-hidden">
          {signedDocumentStatus === 'processing' && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/75 backdrop-blur-[1px]">
              <div className="mx-4 max-w-sm rounded-xl border border-amber-200 bg-white px-5 py-4 text-center shadow-lg">
                <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-amber-200 border-t-amber-600" />
                <p className="text-sm font-semibold text-gray-900">Gerando documento assinado</p>
                <p className="mt-1 text-xs leading-relaxed text-gray-600">
                  Sua assinatura já está registrada. Estamos consolidando o PDF com as evidências
                  visuais.
                </p>
              </div>
            </div>
          )}
          {signedDocumentStatus === 'failed' && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/80 backdrop-blur-[1px]">
              <div className="mx-4 max-w-sm rounded-xl border border-red-200 bg-white px-5 py-4 text-center shadow-lg">
                <p className="text-sm font-semibold text-red-800">Falha ao gerar o PDF consolidado</p>
                <p className="mt-1 text-xs leading-relaxed text-gray-600">
                  Sua assinatura permanece válida. Você pode tentar gerar o documento novamente.
                </p>
                {onRetryFinalPdf && (
                  <button
                    type="button"
                    onClick={onRetryFinalPdf}
                    className="mt-3 inline-flex items-center justify-center rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700"
                  >
                    Tentar novamente
                  </button>
                )}
              </div>
            </div>
          )}
          {edocHtml !== null ? (
            <div
              ref={viewerRef}
              className="h-full w-full overflow-auto p-4 bg-gray-50"
              style={{
                scrollbarWidth: 'thin',
                scrollbarColor: '#cbd5e0 #f7fafc',
                scrollBehavior: 'smooth'
              }}
            >
              <style>{readOnlyQuillStyles}</style>
              <div className="quill-readonly-content bg-white rounded-md border border-gray-200">
                <ReactQuill
                  key={document.id}
                  value={edocHtml}
                  readOnly
                  theme="snow"
                  modules={readOnlyModules}
                />
              </div>
            </div>
          ) : (
          <div 
            ref={viewerRef}
            dangerouslySetInnerHTML={{ __html: content }}
            className="h-full w-full overflow-auto p-4 bg-gray-50"
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: '#cbd5e0 #f7fafc',
              scrollBehavior: 'smooth'
            }}
          />
          )}
          
          {/* Scroll Indicators */}
          <div className="absolute top-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded opacity-0 hover:opacity-100 transition-opacity">
            Use scroll ou setas para navegar
          </div>
          
          {/* Scroll Position Indicator */}
          <div className="absolute bottom-2 right-2 bg-white bg-opacity-90 text-gray-600 text-xs px-2 py-1 rounded shadow">
            <div className="flex items-center space-x-2">
              <span>Scroll: </span>
              <div className="w-16 h-1 bg-gray-300 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-blue-500 transition-all duration-200"
                  style={{ width: '0%' }}
                  id="scroll-indicator"
                />
              </div>
            </div>
          </div>
          </div>
        </div>
        
        {/* Footer com zoom — oculto no modo embutido */}
        {!embedded && <div className="border-t bg-gray-50 p-3 flex justify-between items-center rounded-b-lg">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                const viewer = viewerRef.current;
                if (viewer) {
                  viewer.style.transform = 'scale(0.8)';
                  viewer.style.transformOrigin = 'top left';
                }
              }}
              className="text-gray-500 hover:text-gray-700 p-1 rounded hover:bg-gray-200"
              title="Diminuir zoom"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
              </svg>
            </button>
            <span className="text-sm text-gray-600">100%</span>
            <button
              onClick={() => {
                const viewer = viewerRef.current;
                if (viewer) {
                  viewer.style.transform = 'scale(1.2)';
                  viewer.style.transformOrigin = 'top left';
                }
              }}
              className="text-gray-500 hover:text-gray-700 p-1 rounded hover:bg-gray-200"
              title="Aumentar zoom"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </button>
            <button
              onClick={() => {
                const viewer = viewerRef.current;
                if (viewer) {
                  viewer.style.transform = 'scale(1)';
                  viewer.style.transformOrigin = 'top left';
                }
              }}
              className="text-gray-500 hover:text-gray-700 p-1 rounded hover:bg-gray-200 ml-2"
              title="Resetar zoom"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
          
          <div className="text-sm text-gray-500">
            Use as setas do teclado ou scroll do mouse para navegar
          </div>
        </div>}
      </div>
    </div>
  );
};

export default DocumentViewer;
